/**
 * Groups Insights — the Groups P6 "Prove" dashboard (docs/groups/GROUPS-SPEC.md
 * §8: the phase exits when a metrics dashboard is live). One round trip to
 * public.groups_insights(p_days) (20261017140000_groups_insights.sql) gives
 * supply, membership, meetup health, the GRP-ANL-1 funnel and the top groups
 * by new members over a 7/30/90-day window.
 *
 * Fetched with a plain useState/useEffect + supabaseClient.rpc() call — the
 * same pattern group_ideas/list.tsx and group_chapters/list.tsx use for
 * RPC-backed pages — rather than a Refine data hook, since this isn't a CRUD
 * resource. A second, parallel read of feature_flags.groups_enabled decides
 * the empty state: if Groups is off for members AND every count in the
 * window is zero, that is expected (nobody can generate activity yet), not a
 * broken chart.
 *
 * Cards are capped at 720px (GRP-ADM-4; the owner's admin viewport is
 * 3440px — see groups_settings/page.tsx and community_manager/controls/page.tsx
 * for the same convention). The KPI strip itself is left full-width, matching
 * every other ListPage on this admin.
 */
import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Segmented, Table } from 'antd'
import { Line } from '@ant-design/plots'
import { supabaseClient } from '../../lib/supabaseClient'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, EmptyHint, TableSurface, type Stat } from '../../theme/listkit'
import {
  Rocket,
  GraduationCap,
  UsersRound,
  Target,
  Clock,
  CalendarDays,
  Flag,
  Sparkles,
} from '../../components/icons'

const CARD = { maxWidth: 720 } as const

export const GROUPS_INSIGHTS_WINDOW_OPTIONS = [7, 30, 90] as const
export type GroupsInsightsWindow = (typeof GROUPS_INSIGHTS_WINDOW_OPTIONS)[number]

export interface FunnelRow {
  event_name: string
  count: number
  distinct_users: number
}
export interface TopGroupRow {
  id: string
  name: string
  slug: string
  member_count: number
  new_members: number
}
export interface GroupsInsightsData {
  window_days: number
  generated_at: string
  supply: {
    active_interest_groups: number
    chapters_with_members: number
    by_status: Record<string, number>
    pending_proposals: number
    open_ideas: number
    total_pledges: number
  }
  membership: {
    total_active_memberships: number
    distinct_interest_members: number
    pct_members_in_group: number
    joins_per_day: { day: string; count: number }[]
    pending_join_requests: number
    median_request_decision_hours: number | null
  }
  meetups: {
    upcoming_count: number
    held_in_window: number
    avg_rsvp_going: number
    avg_rsvp_maybe: number
  }
  funnel: FunnelRow[]
  top_groups: TopGroupRow[]
}

/** True when every headline number in the payload is zero — the shape an unlaunched Groups gives. */
export function isAllZero(d: GroupsInsightsData): boolean {
  return (
    d.supply.active_interest_groups === 0 &&
    d.supply.chapters_with_members === 0 &&
    d.supply.pending_proposals === 0 &&
    d.supply.open_ideas === 0 &&
    d.supply.total_pledges === 0 &&
    d.membership.total_active_memberships === 0 &&
    d.membership.pending_join_requests === 0 &&
    d.meetups.upcoming_count === 0 &&
    d.meetups.held_in_window === 0 &&
    d.funnel.every((f) => f.count === 0) &&
    d.top_groups.length === 0
  )
}

const cardTitle: React.CSSProperties = {
  fontFamily: brand.fDisplay,
  fontWeight: 700,
  fontSize: 15,
  color: brand.ink,
  marginBottom: 12,
}
const inlineStat: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 600,
  fontSize: 12,
  color: brand.sub,
  marginBottom: 12,
}
const errorBanner: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 600,
  fontSize: 13,
  color: '#a4452f',
  background: '#f6e4de',
  border: '1px solid rgba(164,69,47,.25)',
  borderRadius: 12,
  padding: '14px 16px',
  marginBottom: 18,
}

export function GroupsInsightsPage() {
  const [days, setDays] = useState<GroupsInsightsWindow>(30)
  const [data, setData] = useState<GroupsInsightsData | null>(null)
  const [groupsEnabled, setGroupsEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (p_days: GroupsInsightsWindow) => {
    setLoading(true)
    setError(null)
    const [rpcResult, flagResult] = await Promise.all([
      supabaseClient.rpc('groups_insights', { p_days }),
      supabaseClient.from('feature_flags').select('enabled').eq('key', 'groups_enabled').maybeSingle(),
    ])
    if (rpcResult.error) {
      setError(rpcResult.error.message ?? 'Could not load Groups insights.')
      setLoading(false)
      return
    }
    setData(rpcResult.data as unknown as GroupsInsightsData)
    setGroupsEnabled(Boolean((flagResult.data as { enabled?: boolean } | null)?.enabled))
    setLoading(false)
  }, [])

  useEffect(() => {
    load(days)
  }, [days, load])

  const showEmptyState = !loading && !error && data && !groupsEnabled && isAllZero(data)

  const stats: Stat[] | undefined = data
    ? [
        { label: 'Active Groups', value: data.supply.active_interest_groups, icon: Rocket, tone: 'gold' },
        { label: 'Chapters w/ Members', value: data.supply.chapters_with_members, icon: GraduationCap, tone: 'jaguar' },
        { label: 'Active Memberships', value: data.membership.total_active_memberships, icon: UsersRound, tone: 'jaguar' },
        { label: '% Members in a Group', value: `${data.membership.pct_members_in_group}%`, icon: Target, tone: 'gold' },
        { label: 'Pending Join Requests', value: data.membership.pending_join_requests, icon: Clock, tone: 'neutral' },
        { label: 'Upcoming Meetups', value: data.meetups.upcoming_count, icon: CalendarDays, tone: 'green' },
        { label: 'Pending Proposals', value: data.supply.pending_proposals, icon: Flag, tone: 'neutral' },
        { label: 'Open Ideas', value: data.supply.open_ideas, icon: Sparkles, tone: 'gold' },
      ]
    : undefined

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Groups',
        title: 'Insights',
        description:
          'Adoption since launch: who joined, which groups are growing, what members do in Groups, and how meetups are going. Numbers cover the selected trailing window.',
        actions: (
          <Segmented<GroupsInsightsWindow>
            data-testid="groups-insights-window"
            value={days}
            onChange={(v) => setDays(v)}
            options={GROUPS_INSIGHTS_WINDOW_OPTIONS.map((d) => ({ label: `${d} days`, value: d }))}
          />
        ),
      }}
      stats={stats}
      statColumns={4}
    >
      {error ? <div style={errorBanner}>{error}</div> : null}

      {loading && !data ? (
        <SectionCard title="Loading" style={CARD}>
          <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.sub, padding: '12px 0' }}>
            Loading Groups insights…
          </div>
        </SectionCard>
      ) : null}

      {showEmptyState ? (
        <SectionCard style={CARD}>
          <EmptyHint>No Groups activity yet — Groups is switched off for members.</EmptyHint>
        </SectionCard>
      ) : null}

      {data && !showEmptyState ? (
        <>
          <SectionCard title="Joins Per Day" sub={`Trailing ${data.window_days} days`} style={CARD}>
            <div style={inlineStat}>
              Median request → decision:{' '}
              {data.membership.median_request_decision_hours == null
                ? 'n/a'
                : `${data.membership.median_request_decision_hours}h`}
            </div>
            {data.membership.joins_per_day.length > 0 ? (
              <Line
                data={data.membership.joins_per_day}
                xField="day"
                yField="count"
                height={200}
                autoFit
                style={{ stroke: brand.gold, lineWidth: 2 }}
              />
            ) : (
              <EmptyHint>No joins in this window.</EmptyHint>
            )}
          </SectionCard>

          <div style={{ height: 20 }} />

          <SectionCard title="Meetups" style={CARD}>
            <div style={inlineStat}>
              Held in window: {data.meetups.held_in_window} · Avg going: {data.meetups.avg_rsvp_going} · Avg maybe:{' '}
              {data.meetups.avg_rsvp_maybe}
            </div>
          </SectionCard>

          <div style={{ height: 20 }} />

          <SectionCard title="Groups Funnel" sub="GRP-ANL-1 events (excludes video)" style={CARD}>
            {data.funnel.length > 0 ? (
              <TableSurface>
                <Table dataSource={data.funnel} rowKey="event_name" pagination={false} size="middle">
                  <Table.Column title="Event" dataIndex="event_name" />
                  <Table.Column
                    title="Count"
                    dataIndex="count"
                    align="right"
                    render={(v: number) => v.toLocaleString()}
                  />
                  <Table.Column
                    title="Distinct Users"
                    dataIndex="distinct_users"
                    align="right"
                    render={(v: number) => v.toLocaleString()}
                  />
                </Table>
              </TableSurface>
            ) : (
              <EmptyHint>No Groups activity yet in this window.</EmptyHint>
            )}
          </SectionCard>

          <div style={{ height: 20 }} />

          <SectionCard title="Top Groups" sub={`New members in the last ${data.window_days} days`} style={CARD}>
            {data.top_groups.length > 0 ? (
              <TableSurface>
                <Table dataSource={data.top_groups} rowKey="id" pagination={false} size="middle">
                  <Table.Column
                    title="Group"
                    dataIndex="name"
                    render={(name: string, r: TopGroupRow) => (
                      <Link to={`/groups/show/${r.id}`} style={{ fontWeight: 600, color: brand.ink }}>
                        {name}
                      </Link>
                    )}
                  />
                  <Table.Column title="Slug" dataIndex="slug" />
                  <Table.Column
                    title="Members"
                    dataIndex="member_count"
                    align="right"
                    render={(v: number) => v.toLocaleString()}
                  />
                  <Table.Column
                    title="New Members"
                    dataIndex="new_members"
                    align="right"
                    render={(v: number) => v.toLocaleString()}
                  />
                </Table>
              </TableSurface>
            ) : (
              <EmptyHint>No new members in this window.</EmptyHint>
            )}
          </SectionCard>
        </>
      ) : null}
    </ListPage>
  )
}
